/**
 * SEO Configuration for GramCore Public Website
 * 
 * Centralized SEO metadata and configuration for all pages.
 * Provides default values and helper functions for consistent SEO across the site.
 */

export interface SEOConfig {
  title?: string;
  description?: string;
  keywords?: string[];
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product' | 'profile';
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
}

export interface RouteSEO {
  path: string;
  title: string;
  description: string;
  keywords?: string[];
  ogType?: SEOConfig['ogType'];
  priority?: number;
  changeFreq?: 'always' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'yearly' | 'never';
}

/**
 * Site-wide SEO defaults
 */
export const SITE_CONFIG = {
  name: 'Gramline',
  defaultTitle: 'Gramline | No.1 Steel Privacy Fencing Australia',
  defaultDescription: 'Australia\'s leading supplier of steel privacy fencing, security fencing, and pool fencing. Quality products, expert installation, and exceptional service.',
  siteUrl: 'https://gram.com.au',
  twitterHandle: '@gramline',
  locale: 'en_AU',
  defaultOgImage: '/home-slideshow/01.jpg',
} as const;

/**
 * Route-to-SEO mapping for consistent metadata
 */
export const ROUTE_SEO: Record<string, RouteSEO> = {
  '/': {
    path: '/',
    title: 'Gramline | No.1 Steel Privacy Fencing Australia',
    description: 'Australia\'s leading supplier of steel privacy fencing, security fencing, and pool fencing. Quality products, expert installation, and exceptional service.',
    keywords: ['steel privacy fencing', 'security fencing', 'pool fencing', 'fencing australia', 'gramline'],
    priority: 1.0,
    changeFreq: 'weekly',
  },
  '/products/spf-steel': {
    path: '/products/spf-steel',
    title: 'SPF Steel Privacy Fencing | Gramline',
    description: 'Premium steel privacy fencing systems. Durable, secure, and stylish solutions for residential and commercial properties.',
    keywords: ['steel privacy fencing', 'SPF', 'privacy fence', 'steel fencing'],
    ogType: 'product',
    priority: 0.9,
    changeFreq: 'monthly',
  },
  '/products/pool-fencing': {
    path: '/products/pool-fencing',
    title: 'Pool Fencing | Childgard Pool Fencing | Gramline',
    description: 'Compliant pool fencing solutions meeting Australian standards. Safe, secure, and stylish pool barriers for your home.',
    keywords: ['pool fencing', 'childgard', 'pool barrier', 'safety fencing'],
    ogType: 'product',
    priority: 0.9,
    changeFreq: 'monthly',
  },
  '/products/garden-fencing': {
    path: '/products/garden-fencing',
    title: 'Garden Fencing | Childgard Garden Fencing | Gramline',
    description: 'Beautiful garden fencing solutions. Enhance your outdoor space with stylish and durable fencing options.',
    keywords: ['garden fencing', 'residential fencing', 'decorative fencing'],
    ogType: 'product',
    priority: 0.9,
    changeFreq: 'monthly',
  },
  '/contact': {
    path: '/contact',
    title: 'Contact Us | Gramline',
    description: 'Get in touch with Gramline. Contact our team for product inquiries, quotes, and customer support.',
    keywords: ['contact gramline', 'fencing quotes', 'customer support'],
    priority: 0.8,
    changeFreq: 'monthly',
  },
  '/about': {
    path: '/about',
    title: 'About Gramline | Leading Fencing Supplier',
    description: 'Learn about Gramline - Australia\'s trusted fencing supplier with decades of experience in quality steel fencing solutions.',
    keywords: ['about gramline', 'fencing company', 'australian fencing'],
    priority: 0.7,
    changeFreq: 'monthly',
  },
  '/specification/colours': {
    path: '/specification/colours',
    title: 'Colour Specifications | Fencing Colours | Gramline',
    description: 'Explore our range of fencing colours and finishes. Find the perfect colour to match your property.',
    keywords: ['fencing colours', 'colour specifications', 'powder coating'],
    priority: 0.8,
    changeFreq: 'monthly',
  },
};

/**
 * Generate full page title
 */
export function generateTitle(pageTitle?: string): string {
  if (!pageTitle) return SITE_CONFIG.defaultTitle;
  if (pageTitle.includes('Gramline') || pageTitle.includes('|')) {
    return pageTitle;
  }
  return `${pageTitle} | ${SITE_CONFIG.name}`;
}

/**
 * Generate canonical URL
 */
export function generateCanonical(path: string): string {
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_CONFIG.siteUrl}${cleanPath}`;
}

/**
 * Generate Open Graph image URL
 */
export function generateOgImage(imagePath?: string): string {
  if (!imagePath) return `${SITE_CONFIG.siteUrl}${SITE_CONFIG.defaultOgImage}`;
  if (imagePath.startsWith('http')) return imagePath;
  if (imagePath.startsWith('/')) return `${SITE_CONFIG.siteUrl}${imagePath}`;
  return `${SITE_CONFIG.siteUrl}/${imagePath}`;
}

/**
 * Get SEO config for a route
 */
export function getRouteSEO(path: string): RouteSEO | undefined {
  // Exact match first
  if (ROUTE_SEO[path]) return ROUTE_SEO[path];
  
  // Try matching with trailing slash
  const pathWithSlash = path.endsWith('/') ? path : `${path}/`;
  if (ROUTE_SEO[pathWithSlash]) return ROUTE_SEO[pathWithSlash];
  
  // Try matching without trailing slash
  const pathWithoutSlash = path.endsWith('/') ? path.slice(0, -1) : path;
  if (ROUTE_SEO[pathWithoutSlash]) return ROUTE_SEO[pathWithoutSlash];
  
  return undefined;
}

/**
 * Merge route SEO with custom overrides
 */
export function mergeSEO(routeSEO?: RouteSEO, overrides?: Partial<SEOConfig>): SEOConfig {
  const config: SEOConfig = {
    title: routeSEO?.title || SITE_CONFIG.defaultTitle,
    description: routeSEO?.description || SITE_CONFIG.defaultDescription,
    keywords: routeSEO?.keywords || [],
    ogType: routeSEO?.ogType || 'website',
    canonical: routeSEO?.path ? generateCanonical(routeSEO.path) : undefined,
  };
  
  if (overrides) {
    return {
      ...config,
      ...overrides,
      title: overrides.title ? generateTitle(overrides.title) : config.title,
      canonical: overrides.canonical || config.canonical,
      keywords: overrides.keywords || config.keywords,
    };
  }
  
  return config;
}